import { gql } from "@apollo/client";

export const PAGE_INFO = gql`
	fragment pageInfoFields on PageInfo {
		endCursor
		totalCount
		hasNextPage
	}
`;

export const CURRENCY = gql`
	fragment currencyFields on Currency {
		id
		name
		symbol
		abbreviation
		decimalSeparator
		thousandsSeparator
	}
`;

export const MONEY = gql`
	fragment moneyFields on Money {
		name
		value
		currency {
			...currencyFields
		}
	}
	${CURRENCY}
`;

export const MONEY_SERIES_POINT = gql`
	fragment MoneySeriesPointFields on MoneySeriesPoint {
		date
		value {
			...moneyFields
		}
	}
	${MONEY}
`;

export const REMOTE_ASSET = gql`
	fragment remoteAssetFields on RemoteAsset {
		id
		index
		size
		name
		url
	}
`;

const ADDRESS = gql`
	fragment addressFields on Address {
		id
		addressLine1
		addressLine2
		suburb
		city
		postCode
		country {
			id
			name
			isoAlpha2
		}
		status
	}
`;

export const HOLDING_DOCUMENT = gql`
	fragment holdingDocumentFields on HoldingDocument {
		title
		published
		file {
			...remoteAssetFields
		}
		category {
			id
			name
		}
	}
	${REMOTE_ASSET}
`;

export const BANK_ACCOUNT = gql`
	fragment bankAccountFields on BankAccount {
		id
		name
		accountNumber
		bsbCode
		ukSortCode
		routingNumber
		locked
		currency {
			...currencyFields
		}
	}
	${CURRENCY}
`;

export const VERIFIABLE_BANK_ACCOUNT = gql`
	fragment verifiableBankAccountFields on VerifiableBankAccount {
		id
		created
		name
		nickname
		status
		currency {
			...currencyFields
		}
		... on NZDVerifiableBankAccount {
			accountNumber
		}
		... on AUDVerifiableBankAccount {
			accountNumber
			bsbCode
		}
		... on USDVerifiableBankAccount {
			accountNumber
			routingNumber
		}
		... on GBPVerifiableBankAccount {
			accountNumber
			sortCode
		}
		... on EURVerifiableBankAccount {
			accountNumber
		}
	}
	${CURRENCY}
`;

export const BENEFICIAL_OWNER = gql`
	fragment beneficialOwnerFields on BeneficialOwner {
		id
		email
		firstName
		middleName
		lastName
		investingEntityName
		investingEntityPrimaryOwnerFirstName
		investingEntityPrimaryOwnerLastName
		addressVerificationStatus
		basicDetailsVerificationStatus
		identityDocumentVerificationStatus
		biometricIdentityVerificationStatus
		address {
			...addressFields
			placeId
		}
	}
	${ADDRESS}
`;

export const ACCOUNT = gql`
	fragment accountFields on Account {
		id
		firstName
		middleName
		lastName
		preferredName
		email
		dayOfBirth
		monthOfBirth
		yearOfBirth
		availableFunds
		timeZone
		frontChatHash
		isAccountLocked
		accountLockedReason
		hasProvidedAccountDetails
		phoneNumber {
			phoneNumber
			callingCode
		}
		country {
			id
			callingCode
		}
		address {
			...addressFields
		}
		placeOfBirth {
			id
			city
			country {
				id
				name
			}
		}
		primaryCitizenship {
			id
			name
		}
	}
	${ADDRESS}
`;

export const SESSION = gql`
	fragment sessionFields on Session {
		id
		token
		expires
		impersonatedBy {
			id
			firstName
			lastName
		}
	}
`;

export const FEATURE_FLAG = gql`
	fragment featureFlagFields on FeatureFlag {
		name
		isEnabled
	}
`;

export const ACCREDITATION = gql`
	fragment accreditationFields on Accreditation {
		id
		updated
		country {
			id
			name
		}
		... on WholesaleAccreditation {
			status
			document {
				viewURL
				template
				signatures {
					id
					isMe
					name
					email
					status
					signingURL
					relationship
				}
			}
		}
	}
`;

export const INVESTING_ENTITY = gql`
	fragment investingEntityFields on InvestingEntity {
		id
		number
		address
		entityTypeEnum
		trustType
		companyType
		partnershipType
		sourceOfFunds
		relationshipEnum
		taxIdentificationNumber
		nzPrescribedInvestorRate
		frequencyOfInvestment
		reasonForInvesting
		canBeReset
		country {
			id
			name
		}
		accreditationCountry {
			id
			name
		}
		accreditation {
			...accreditationFields
		}
		bankAccounts {
			...verifiableBankAccountFields
		}
		documents {
			...remoteAssetFields
		}
	}
	${REMOTE_ASSET}
	${ACCREDITATION}
	${VERIFIABLE_BANK_ACCOUNT}
`;

export const ACCOUNT_NOTIFICATION = gql`
	fragment accountNotificationFields on AccountNotification {
		id
		status
		sortOrder
	}
`;

export const INVESTING_ENTITY_NOTIFICATION = gql`
	fragment investingEntityNotificationFields on InvestingEntityNotification {
		id
		status
		sortOrder
		... on VerifyNZDBankAccountNotification {
			bankAccount {
				...verifiableBankAccountFields
			}
		}
		... on VerifyAUDBankAccountNotification {
			bankAccount {
				...verifiableBankAccountFields
			}
		}
		... on VerifyEURBankAccountNotification {
			bankAccount {
				...verifiableBankAccountFields
			}
		}
		... on VerifyGBPBankAccountNotification {
			bankAccount {
				...verifiableBankAccountFields
			}
		}
		... on VerifyUSDBankAccountNotification {
			bankAccount {
				...verifiableBankAccountFields
			}
		}
		... on ConfirmUnsupportedCountryAccreditationNotification {
			country {
				id
				name
			}
		}
	}
	${VERIFIABLE_BANK_ACCOUNT}
`;
